import { useState, useEffect, useCallback, useRef } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { UserProfile } from '@/types/profile';
import { unifiedSessionService } from '@/services/unifiedSessionService';
import { useProfileManager } from './useProfileManager';

/**
 * Main hook for managing authentication state
 */
export const useAuthState = () => {
  // State for auth data
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpiration, setSessionExpiration] = useState<Date | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Last activity tracking
  const [lastActivity, setLastActivity] = useState<Date>(new Date());
  
  // Stable references to prevent re-renders
  const stableProfileRef = useRef<UserProfile | null>(null);
  
  // Use optimized profile manager
  const { fetchProfile: fetchProfileManager, getCachedProfile } = useProfileManager();
  
  // Simplified activity update using unified session service
  const updateActivity = useCallback(() => {
    const now = new Date();
    setLastActivity(now);
    unifiedSessionService.updateLastActivity();
  }, []);
  
  /**
   * Optimized profile fetching with stable references
   */
  const fetchProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    try {
      console.log("🔍 useAuthState.fetchProfile called for user:", userId);
      
      // Check if we already have this profile and it's the same user AND it's a valid profile object
      if (stableProfileRef.current && 
          stableProfileRef.current.id === userId && 
          stableProfileRef.current.username !== undefined) {
        console.log("✅ Using valid stable profile ref:", stableProfileRef.current.id);
        return stableProfileRef.current;
      }

      console.log("🔄 Calling UserService.fetchProfile...");
      const freshProfile = await fetchProfileManager(userId);
      console.log("📊 UserService.fetchProfile returned:", freshProfile);
      
      if (freshProfile) {
        // Only update state if profile actually changed
        if (!stableProfileRef.current || stableProfileRef.current.id !== freshProfile.id) {
          console.log("✅ Setting new profile in auth state:", freshProfile.id);
          stableProfileRef.current = freshProfile;
          setProfile(freshProfile);
          
          // Check admin status from secure user_roles table
          try {
            const { data: roleData } = await supabase
              .from('user_roles')
              .select('role')
              .eq('user_id', userId)
              .eq('role', 'admin')
              .maybeSingle();
            
            setIsAdmin(!!roleData);
          } catch (error) {
            console.error('Error checking admin status:', error);
            setIsAdmin(false);
          }
        } else {
          console.log("⚠️ Profile unchanged, not updating state");
        }
        setIsLoading(false);
        return freshProfile;
      }
      
      console.log("❌ No profile returned from UserService");
      setIsLoading(false);
      return null;
    } catch (error: any) {
      console.error('💥 Error in useAuthState.fetchProfile:', error.message);
      setIsLoading(false);
      return null;
    }
  }, [fetchProfileManager]);
  
  // Initialize auth state with unified session service
  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;
    
    const initializeAuth = async () => {
      setIsLoading(true);
      
      try {
        // Use unified session service for consistent session management
        subscription = unifiedSessionService.initializeSession(
          async (session) => {
            // Handle sign in
            setSession(session);
            setUser(session.user);
            
            if (session.expires_at) {
              setSessionExpiration(new Date(session.expires_at * 1000));
            }
            
            // Fetch profile (admin status is set within fetchProfile)
            await fetchProfile(session.user.id);
            
            updateActivity();
          },
          () => {
            // Handle sign out
            console.log("🔄 Clearing auth state on sign out");
            setSession(null);
            setUser(null);
            setProfile(null);
            setIsAdmin(false);
            setSessionExpiration(null);
            // Clear the stable profile ref to force fresh fetch on next login
            stableProfileRef.current = null;
          }
        );
        
        // Check for existing session
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (currentSession) {
          setSession(currentSession);
          setUser(currentSession.user);
          
          if (currentSession.expires_at) {
            setSessionExpiration(new Date(currentSession.expires_at * 1000));
          }
          
          // Fetch profile (admin status is set within fetchProfile)
          await fetchProfile(currentSession.user.id);
        }
        
      } catch (error: any) {
        console.error('Error initializing auth:', error.message);
        setError(error.message);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };
    
    initializeAuth();
    
    return () => {
      subscription?.unsubscribe();
    };
  }, [fetchProfile, updateActivity]);
  
  // Derived auth state
  const isAuthenticated = !!user && !!session;
  
  return {
    session,
    user,
    profile,
    isLoading,
    isInitialized,
    isAuthenticated,
    isAdmin,
    error,
    lastActivity,
    sessionExpiration,
    setSession,
    setUser,
    setProfile,
    setIsLoading,
    setIsInitialized,
    fetchProfile,
    updateLastActivity: updateActivity
  };
};
